import { mergeAttributes, Node } from "@tiptap/core";
import { Node as ProseMirrorNode } from "@tiptap/pm/model";
import { Plugin, PluginKey } from "@tiptap/pm/state";
import { Decoration, DecorationSet } from "@tiptap/pm/view";

interface AffixOptions {
  HTMLAttributes: Record<string, unknown>;
}

const NODE_NAME = "affix";

const pluginKey = new PluginKey("affixModifiedTracker");

const buildDecorations = (doc: ProseMirrorNode): DecorationSet => {
  const decorations: Decoration[] = [];

  doc.descendants((node, pos) => {
    if (node.type.name !== NODE_NAME) return;

    const { originalValue } = node.attrs;
    if (!originalValue) return;

    if (node.textContent !== originalValue) {
      decorations.push(
        Decoration.node(pos, pos + node.nodeSize, { class: "modified" }),
      );
    }
  });

  return DecorationSet.create(doc, decorations);
};

export const Affix = Node.create<AffixOptions>({
  name: NODE_NAME,
  priority: 1000,
  group: "block",
  content: "inline*",

  addAttributes() {
    return {
      originalValue: {
        default: null,
        parseHTML: (element) => element.getAttribute("data-original-value"),
        renderHTML: () => ({}),
      },
    };
  },

  parseHTML() {
    return [{ tag: this.name }];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      this.name,
      mergeAttributes(this.options.HTMLAttributes, HTMLAttributes),
      0,
    ];
  },

  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: pluginKey,
        state: {
          init: (_, state) => buildDecorations(state.doc),
          apply: (tr, old) => (tr.docChanged ? buildDecorations(tr.doc) : old),
        },
        props: {
          decorations: (state) => pluginKey.getState(state),
        },
      }),
    ];
  },
});
